import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ProductCard from './ProductCard';
import { request } from '../utils/request';
import { Product as MockProduct, Category } from '../data/mockData';
import { ApiProduct, ApiTemplate } from '../utils/resourceUtils';
import { 
  ShoppingBagIcon, 
  SparklesIcon, 
  TruckIcon, 
  ShieldCheckIcon,
  PaintBrushIcon,
  ExclamationTriangleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  PlusIcon,
  ArrowUpIcon,
  ChevronDownIcon,
  ArrowTopRightOnSquareIcon
} from '@heroicons/react/24/outline';

// Interface for featured products API response
interface FeaturedProductsResponse {
  success: boolean;
  code: number;
  message: string;
  result: {
    products: ApiProduct[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
}

// Interface for categories API response
interface CategoriesResponse {
  success: boolean;
  code: number;
  message: string;
  result: Category[];
}

// Interface for carousel item
interface CarouselItem {
  background: string;
  title: string;
  description: string;
  url: string;
}

const customerTestimonials = [
  {
    image: 'https://gmpublic.oss-us-west-1.aliyuncs.com/ads/allset.jpg',
    name: 'Emily',
    quote: 'So easy to turn my idea into a custom gift set. The final result looked even better than I expected.'
  },
  {
    image: 'https://gmpublic.oss-us-west-1.aliyuncs.com/ads/vintage.jpg',
    name: 'Jason',
    quote: 'I used it for a vintage-style design and the workflow was fast, smooth, and perfect for gifting.'
  },
  {
    image: 'https://gmpublic.oss-us-west-1.aliyuncs.com/ads/wansheng.jpg',
    name: 'Sophia',
    quote: 'The Halloween design came out great. It saved me a lot of time when preparing gifts in batches.'
  },
  {
    image: 'https://gmpublic.oss-us-west-1.aliyuncs.com/ads/wenshen.jpg',
    name: 'Michael',
    quote: 'Uploading images and customizing products was simple. I will definitely use it again for more gift ideas.'
  }
] as const;

const HOME_AI_PROMPT_STORAGE_KEY = 'giftmkr:home-ai-prompt';

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [featuredProducts, setFeaturedProducts] = useState<MockProduct[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);
  
  // Carousel state
  const [carouselItems, setCarouselItems] = useState<CarouselItem[]>([]);
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);
  const [isCarouselAutoPlaying, setIsCarouselAutoPlaying] = useState(true);
  const [homeAiPrompt, setHomeAiPrompt] = useState('');

  const handleHomeAiSubmit = () => {
    const prompt = homeAiPrompt.trim();
    if (!prompt) {
      return;
    }

    sessionStorage.setItem(HOME_AI_PROMPT_STORAGE_KEY, prompt);
    navigate('/products?ai=tee');
  };

  // Load categories from API
  const loadCategories = async () => {
    setIsLoadingCategories(true);
    setCategoriesError(null);
    
    try {
      const response: CategoriesResponse = await request({
        url: 'categories',
        method: 'GET'
      });
      
      if (response.success) {
        setCategories(response.result);
      } else {
        setCategoriesError('Failed to load categories');
      }
    } catch (error) {
      console.error('Load categories error:', error);
      setCategoriesError('Failed to load categories');
    } finally {
      setIsLoadingCategories(false);
    }
  };
 

  // Carousel navigation functions
  const goToNextSlide = () => {
    setCurrentCarouselIndex((prev) => 
      prev === carouselItems.length - 1 ? 0 : prev + 1
    );
  };

  const goToPrevSlide = () => {
    setCurrentCarouselIndex((prev) => 
      prev === 0 ? carouselItems.length - 1 : prev - 1
    );
  };

  const goToSlide = (index: number) => {
    setCurrentCarouselIndex(index);
  };

  // Auto-play carousel
  useEffect(() => {
    if (!isCarouselAutoPlaying || carouselItems.length <= 1) return;

    const interval = setInterval(() => {
      goToNextSlide();
    }, 5000); // Change slide every 5 seconds

    return () => clearInterval(interval);
  }, [currentCarouselIndex, isCarouselAutoPlaying, carouselItems.length]);

  // Load categories and featured products on component mount
  useEffect(() => {
    loadCategories(); 
  }, []); 

  return (
    <div className="min-h-screen bg-gray-50">
      <section className="px-4 pt-8 pb-10 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <div className="rounded-[28px] border border-gray-200 bg-white px-4 py-4 shadow-sm sm:px-6">
            <div className="flex items-center gap-3">
              {/* <button
                type="button"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-gray-700 hover:bg-gray-100"
                aria-label="Add"
              >
                <PlusIcon className="h-6 w-6" />
              </button> */}
              <input
                type="text"
                value={homeAiPrompt}
                onChange={(e) => setHomeAiPrompt(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleHomeAiSubmit();
                  }
                }}
                placeholder="Enter what you want and let AI help design it"
                className="min-w-0 flex-1 appearance-none border-none bg-transparent text-lg text-gray-700 outline-none ring-0 focus:border-none focus:outline-none focus:ring-0 placeholder:text-gray-500 sm:text-2xl"
              />
              <button
                type="button"
                className="hidden items-center gap-2 rounded-full px-3 py-2 text-lg font-semibold text-gray-900 hover:bg-gray-100 sm:flex"
              >
                <span>T-Shirt</span>
                <ChevronDownIcon className="h-5 w-5" />
              </button>
              <button
                type="button"
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-500 transition-colors hover:bg-black hover:text-white active:bg-black active:text-white"
                aria-label="Send"
                onClick={handleHomeAiSubmit}
              >
                <ArrowUpIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="mt-10 rounded-[24px] border border-gray-200 bg-white p-5 shadow-sm sm:p-8">
            <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-3">
                  <h2 className="text-2xl font-semibold text-gray-900">GiftMkr Chrome Extension</h2>
                  <span className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-sm font-medium text-blue-600">
                    Recommended
                  </span>
                </div>
                <p className="mt-3 max-w-2xl text-lg leading-8 text-gray-600">
                  Install the Chrome extension to quickly turn images into gift ideas and batch customize gifts for multiple pictures.
                </p>
              </div>

              <a
                href="https://chromewebstore.google.com/detail/giftmkrmake-image-to-gift/cobjbepghjlhnabahkbhgbidlkohecmk?hl=zh-CN&utm_source=ext_sidebar"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full bg-black px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-gray-800"
              >
                <span>Install Extension</span>
                <ArrowTopRightOnSquareIcon className="h-4 w-4" />
              </a>
            </div>

            <div className="mt-8 rounded-[20px] border border-gray-200 bg-gray-50 p-5">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="rounded-2xl bg-white p-4">
                  <div className="text-sm font-semibold text-gray-900">Batch Friendly</div>
                  <p className="mt-2 text-sm leading-6 text-gray-600">
                    Process multiple pictures faster and keep your gifting workflow consistent.
                  </p>
                </div>
                <div className="rounded-2xl bg-white p-4">
                  <div className="text-sm font-semibold text-gray-900">Image To Gift</div>
                  <p className="mt-2 text-sm leading-6 text-gray-600">
                    Turn inspiration images into personalized gift designs in fewer steps.
                  </p>
                </div>
                <div className="rounded-2xl bg-white p-4">
                  <div className="text-sm font-semibold text-gray-900">Quick Access</div>
                  <p className="mt-2 text-sm leading-6 text-gray-600">
                    Open the extension directly from Chrome whenever you want to customize.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-10">
            <div className="mb-6 text-center">
              <h2 className="text-2xl font-semibold text-gray-900 sm:text-3xl">What Customers Say</h2>
              <p className="mt-2 text-gray-600">
                Real feedback from customers creating personalized gifts with GiftMkr.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 xl:grid-cols-4">
              {customerTestimonials.map((item) => (
                <div
                  key={item.image}
                  className="overflow-hidden rounded-[24px] border border-gray-200 bg-white shadow-sm"
                >
                  <img
                    src={item.image}
                    alt={`${item.name} review`}
                    className="aspect-square w-full object-cover"
                  />
                  <div className="p-5">
                    <div className="text-base font-semibold text-gray-900">{item.name}</div>
                    <p className="mt-2 text-sm leading-6 text-gray-600">{item.quote}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
       
      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">Why Choose Us</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: '#f0f2f5' }}>
                <SparklesIcon className="w-8 h-8" style={{ color: '#2e3a47' }} />
              </div>
              <h3 className="text-lg font-semibold mb-2">Easy Design Tool</h3>
              <p className="text-gray-600">
                Intuitive drag-and-drop interface for creating custom designs
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShieldCheckIcon className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Quality Guarantee</h3>
              <p className="text-gray-600">
                Premium materials and printing quality for lasting results
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <TruckIcon className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Fast Shipping</h3>
              <p className="text-gray-600">
                Quick turnaround times and reliable delivery worldwide
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShoppingBagIcon className="w-8 h-8 text-orange-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Wide Selection</h3>
              <p className="text-gray-600">
                Hundreds of products and templates to choose from
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage; 
